import React, { useEffect, useState, useContext, useCallback, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";

import DeleteModal from "components/shared/DeleteModal";
import AreYouSureModal from "components/shared/AreYouSureModal";
import MessageModal from "components/shared/MessageModal";
import ItemGenerated from "components/shared/ItemGenerated";
import FormFooter from "shared/components/FormElements/FormFooter";
import NotFound from "shared/components/UIElements/NotFound";
import DatePicker from "shared/components/FormElements/DatePicker";
import RouteLimitBlocker from "components/shared/RouteLimitBlocker";

import MapWithTransferTimesContainer from "components/shared/MapWithTransferTimesContainer";
import CreateOppositeServiceButton from "components/shared/CreateOppositeServiceButton";

import Input from "shared/components/FormElements/Input";
import Button from "shared/components/FormElements/Button";
import CustomSelect from "shared/components/FormElements/Select";
import SmsTable from "features/sms/components/SmsTable";
import GenericCard from "components/ui/cards/GenericCard";
import { COMPANY_FEATURES, PLANS, SERVICE_TYPES } from "@routebot/constants";
import { useRouteLimit } from "shared/hooks/useRouteLimit";

import { isArray } from "lodash";

import {
  isActiveOptions_tr,
  isActiveOptions_en,
  personelServiceRouteTypes_tr,
  personelServiceRouteTypes_en,
  yesNoOptions_tr,
  yesNoOptions_en,
} from "shared/util/types";
import { VALIDATOR_REQUIRE, VALIDATOR_TIME_EMPTY_ACCEPTED, VALIDATOR_NONE } from "shared/util/validators";

import { CheckTimeString } from "shared/util/time";
import { checkIsPickUp } from "shared/utility/types";

import { useForm } from "shared/hooks/form-hook";
import { useHttpClient } from "shared/hooks/http-hook";
import { AuthContext } from "shared/context/auth-context";
import { isPhoneAppAdminUiEnabled } from "config/ConfigUtils";
import { StoreContext } from "shared/context/store-context";
import Tabs from "components/ui/tabs/Tabs";
import { getCurrentLanguage } from "shared/utility/lang";

// Driver Scheduler — see: backend/docs/driver-scheduler.md (Sprint 1)
// Bileşen kendi içinde eligibility kontrolü yapar; SHIFT/TRANSFER ve `date`'i olan
// servisler dışında hiçbir şey render etmez.
import AutoAssignButton from "features/driver-scheduler/AutoAssignButton";

import ServiceAuditHistoryModal from "features/services/shared/ServiceAuditHistoryModal";
import ServiceDaysPicker from "shared/components/FormElements/ServiceDaysPicker";

const BasePersonelServiceItem = ({
  itemId,
  onClose,
  isNewItem,
  gotoItem,
  DeleteItem,
  UpdateItem,
  InsertItem,
  config,
}) => {
  //standard code
  const { t } = useTranslation();
  const currentLanguage = getCurrentLanguage();
  const [isItemLoaded, setIsItemLoaded] = useState(false);
  const [loadedItem, setLoadedItem] = useState();
  const [generatedItemId, setGeneratedItemId] = useState();
  const { isLoading, error, sendRequest } = useHttpClient();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteBlockedModal, setShowDeleteBlockedModal] = useState(false);
  const [showRemovePassengersModal, setShowRemovePassengersModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const forceShowMap = true;
  const [showMap, setShowMap] = useState(forceShowMap);
  const showMapRef = useRef(null);

  const [tabValue, setTabValue] = React.useState(0);
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // context
  const auth = useContext(AuthContext);
  const showPhoneAppAdminUi = isPhoneAppAdminUiEnabled(auth?.company?.features);
  const {
    shiftPersonelServices,
    standartPersonelServices,
    users,
    shiftOptions,
    personelSchoolOptions,
    driverOptions,
    personelOptions,
    schools,
    drivers,
  } = useContext(StoreContext);

  // Route limit check for new items
  const { limitStatus, isLimitReached, isUnlimited } = useRouteLimit();
  const shouldBlockNewItem = isNewItem && isLimitReached && !isUnlimited && auth?.company?.plan !== PLANS.UNLIMITED;

  // Get the correct data source based on config
  const getDataSource = () => {
    return config.contextDataKey === "shiftPersonelServices" ? shiftPersonelServices : standartPersonelServices;
  };

  // item info
  const itemType = "personelservice";
  const endPointUpdate = "personelservices";
  const endPointDelete = "personelservices";
  const endPointCreate = "personelservices";

  // item special states
  const [currentUsers, setCurrentUsers] = useState([]);
  const [currentDrivers, setCurrentDrivers] = useState([]);

  // Driver Scheduler — bir sürücü otomatik atandığında CustomSelect'i remount
  // etmek için kullanılır. Sadece auto-assign akışında artar; normal interaksiyonu
  // etkilemez. (See: backend/docs/driver-scheduler.md)
  const [autoAssignTick, setAutoAssignTick] = useState(0);

  const updateCurrentUsers = (data) => {
    if (!data) {
      setCurrentUsers([]);
      return;
    }

    let nowUsers = [];

    let checker = isArray(data) ? data : formState.inputs.users.value;

    for (let i = 0; i < checker.length; i++) {
      const userId = checker[i];
      const user = users.find((item) => item.id === userId);
      if (!nowUsers.includes(user)) {
        nowUsers.push(user);
      }
    }

    const newUsers = [];

    const currentUsersIds = currentUsers.map((item) => item.id);

    for (let i = 0; i < nowUsers.length; i++) {
      const user = nowUsers[i];

      if (currentUsersIds.includes(user.id)) {
        user.transferTime = currentUsers.find((item) => item.id === user.id).transferTime;
      } else {
        if (loadedItem?.usersdetails && loadedItem?.usersdetails?.length > 0) {
          const currentusersdetails = loadedItem.usersdetails.find((item) => item?.user === user.id);
          if (currentusersdetails) {
            user.transferTime = currentusersdetails.transfertime;
          }
        }
      }
      newUsers.push(user);
    }

    setCurrentUsers(newUsers);
  };

  const toggleMap = () => {
    const newShowMapState = !showMap;
    setShowMap(newShowMapState);
    // Haritayı gösterirken butona scroll yap
    if (newShowMapState) {
      setTimeout(() => {
        showMapRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  };

  const updateDrivers = (updatedDrivers) => {
    setCurrentDrivers(
      drivers
        .filter((item) => updatedDrivers?.includes(item.id.toString()))
        .map((item) => {
          return {
            id: item.id,
            name: item.name,
            phonenumber: item.phonenumber,
          };
        }),
    );
  };

  // Driver Scheduler — see: backend/docs/driver-scheduler.md §11
  // Aday modal'dan başarılı atama döndüğünde tüm yerel state'leri ve formState'i
  // senkronize eder. Böylece sonradan "Save" basılsa bile auto-assign overwrite olmaz.
  const handleAutoAssigned = (updatedService, _driverId) => {
    if (!updatedService) return;
    setLoadedItem(updatedService);
    if (typeof UpdateItem === "function") UpdateItem(updatedService);

    const driverIds = (updatedService.drivers || []).map((d) =>
      typeof d === "object" && d ? String(d.id || d._id) : String(d),
    );
    updateDrivers(driverIds);
    inputHandler("drivers", driverIds, true);
    setAutoAssignTick((n) => n + 1);
  };

  // Create form fields based on config
  const createFormFields = () => {
    const baseFields = {
      school: { value: personelSchoolOptions.length > 1 ? "" : personelSchoolOptions[0].value, isValid: true },
      name: { value: null, isValid: true },
      drivers: { value: [], isValid: true },
      plate: { value: "", isValid: true },
      starttime: { value: null, isValid: true },
      stoptime: { value: null, isValid: true },
      users: { value: [], isValid: true },
      transferStartFinishTime: { value: "", isValid: true },
      useUsersDetails: { value: false, isValid: true },
      routeType: { value: "", isValid: true },
      isActive: { value: true, isValid: true },
      serviceDays: { value: [], isValid: true },
    };

    // Add config-specific fields
    if (config.hasShift) {
      baseFields.shift = { value: "", isValid: true };
    }

    if (config.hasDate) {
      baseFields.date = { value: null, isValid: true };
    }

    return baseFields;
  };

  const [formState, inputHandler] = useForm(createFormFields(), true);

  // #region Standard API calls
  const fetchItem = useCallback(async () => {
    setIsItemLoaded(false);
    try {
      const dataSource = getDataSource();
      const currentVehicleRoute = dataSource.find((vehicleRoute) => vehicleRoute.id === itemId);
      setLoadedItem(currentVehicleRoute);

      if (!currentVehicleRoute) {
        setIsItemLoaded(true);
        return;
      }
      setCurrentDrivers(
        drivers
          .filter((item) =>
            currentVehicleRoute?.drivers.map((driverItem) => driverItem.id.toString()).includes(item.id.toString()),
          )
          .map((item) => {
            return {
              id: item.id,
              name: item.name,
              phonenumber: item.phonenumber,
            };
          }),
      );

      setCurrentUsers(
        currentVehicleRoute.users.map((user) => ({
          ...user,
          transferTime:
            currentVehicleRoute.usersdetails.find((order) => order?.user?.toString() === user?.id?.toString())
              ?.transfertime || null,
        })),
      );
    } catch (err) {}
    setIsItemLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, getDataSource(), users]);

  useEffect(() => {
    fetchItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  const handleDeleteClick = useCallback(() => {
    if (config.serviceType === SERVICE_TYPES.TRANSFER_PERSONEL && currentUsers.length > 0) {
      setShowDeleteBlockedModal(true);
      return;
    }
    setShowConfirmModal(true);
  }, [config.serviceType, currentUsers.length]);

  const hasRemovedTransferPassengers = useCallback(() => {
    if (config.serviceType !== SERVICE_TYPES.TRANSFER_PERSONEL || isNewItem || !loadedItem?.users?.length) {
      return false;
    }
    const previousUserIds = new Set(loadedItem.users.map((user) => user?.id?.toString()).filter(Boolean));
    const currentUserIds = new Set(currentUsers.map((user) => user?.id?.toString()).filter(Boolean));
    return [...previousUserIds].some((userId) => !currentUserIds.has(userId));
  }, [config.serviceType, isNewItem, loadedItem, currentUsers]);

  // eslint-disable-next-line
  const performSave = useCallback(async () => {
    const endpoint = isNewItem
      ? `${process.env.REACT_APP_BACKEND_URL}/${endPointCreate}`
      : `${process.env.REACT_APP_BACKEND_URL}/${endPointUpdate}/${itemId}`;

    const postType = isNewItem ? "POST" : "PATCH";

    let usersdetails = [];

    for (let i = 0; i < currentUsers.length; i++) {
      if (currentUsers[i].transferTime) {
        usersdetails.push({
          user: currentUsers[i].id,
          transfertime: currentUsers[i].transferTime,
        });
      }
    }

    // Her iki metrik de YALNIZCA geçerli sayı okunduğunda gönderilir: harita "-" gösterirken
    // ("-" gibi) ham string göndermek backend'de cast hatasıyla kaydı patlatabilirdi.
    let totalDistance;
    try {
      const rawDistance = document.getElementById("totalDistance")?.textContent?.replace("km", "").trim();
      const parsedDistance = parseFloat(rawDistance);
      totalDistance = Number.isFinite(parsedDistance) ? parsedDistance : undefined;
    } catch (err) {
      totalDistance = undefined;
    }

    // totalDuration ekranda gösterilen değerden (dakika) okunur — backend tekrar hesaplamaz.
    let totalDuration;
    try {
      const parsed = parseInt(document.getElementById("totalDuration")?.textContent, 10);
      totalDuration = Number.isFinite(parsed) ? parsed : undefined;
    } catch (err) {
      totalDuration = undefined;
    }

    const infoToBeSent = {
      name: formState.inputs.name.value,
      school: formState.inputs.school.value,
      drivers: formState.inputs.drivers.value,
      plate: formState.inputs.plate.value.trim(),
      starttime: formState.inputs.starttime.value || "00:00",
      stoptime: formState.inputs.stoptime.value || "23:59",
      users: currentUsers.map((user) => user.id),
      routeClientArrivalTime: CheckTimeString(formState.inputs.transferStartFinishTime.value)
        ? formState.inputs.transferStartFinishTime.value
        : undefined,
      usersdetails: usersdetails,
      useUsersDetails: formState.inputs.useUsersDetails.value,
      ...(totalDistance !== undefined ? { totalDistance } : {}),
      ...(totalDuration !== undefined ? { totalDuration } : {}),
    };

    // Add config-specific fields
    if (config.hasShift) {
      infoToBeSent.isshift = true;
      infoToBeSent.shift = formState.inputs.shift.value;
    }

    if (config.hasDate) {
      infoToBeSent.date = formState.inputs.date.value;
    }

    infoToBeSent.routeType = formState.inputs.routeType.value;
    infoToBeSent.isActive = formState.inputs.isActive.value;

    if (config.serviceType) {
      infoToBeSent.serviceType = config.serviceType;
    }

    // Yapıldığı günler yalnızca recurring standart personel servisi için gönderilir
    // (SHIFT/TRANSFER kendi `date`'i ile çalışır). Boş → backend hafta içi varsayar.
    if (config.serviceType === SERVICE_TYPES.PERSONEL) {
      infoToBeSent.serviceDays = Array.isArray(formState.inputs.serviceDays?.value)
        ? formState.inputs.serviceDays.value
        : [];
    }

    //standard code
    try {
      const responseData = await sendRequest(endpoint, postType, JSON.stringify(infoToBeSent));

      if (responseData?.[itemType]) {
        if (isNewItem) {
          InsertItem(responseData[itemType]);
          setGeneratedItemId(responseData[itemType].id);
        } else {
          UpdateItem(responseData[itemType]);
          setLoadedItem(responseData[itemType]);
          window.toast.success(t("updateSuccess"));
        }
      }
    } catch (err) {
      if (err?.message) {
        window.toast.error(err.message);
      } else {
        window.toast.error(t("actionFailed"));
      }
    }
  });

  // eslint-disable-next-line
  const itemUpdateSubmitHandler = useCallback(async (event) => {
    event.preventDefault();

    if (hasRemovedTransferPassengers()) {
      setShowRemovePassengersModal(true);
      return;
    }

    await performSave();
  });

  const memoizedStops = useMemo(() => {
    return currentUsers.map((user) => ({
      ...user,
      location: checkIsPickUp(formState.inputs.routeType?.value) ? user.locationgeton : user.locationgetoff,
    }));
  }, [currentUsers, formState.inputs.routeType?.value]);

  const memoizedOrigin = useMemo(() => {
    return schools.find((school) => formState.inputs.school.value === school.id);
  }, [schools, formState.inputs.school.value]);

  const memoizedTransferStartFinishTime = useMemo(() => {
    const check = CheckTimeString(formState.inputs.transferStartFinishTime.value);
    if (!check) {
      return null;
    }
    return formState.inputs.transferStartFinishTime.value;
  }, [formState.inputs.transferStartFinishTime.value]);

  // Get route type value based on config
  const getRouteTypeValue = () => {
    return formState.inputs.routeType?.value;
  };

  // Get route type options based on config
  const getRouteTypeOptions = () => {
    return currentLanguage === "tr" ? personelServiceRouteTypes_tr : personelServiceRouteTypes_en;
  };

  // Get yes/no options based on config
  const getYesNoOptions = () => {
    return currentLanguage === "tr" ? yesNoOptions_tr : yesNoOptions_en;
  };

  // Get is active options based on config
  const getIsActiveOptions = () => {
    return currentLanguage === "tr" ? isActiveOptions_tr : isActiveOptions_en;
  };

  const personelServiceItemTabs = useMemo(() => {
    // Check if any user doesn't have location information
    const usersWithoutLocation = currentUsers.filter((user) => !user?.locationgeton || !user?.locationgetoff);

    // Check if any user doesn't have phone number (only if user has permission)
    const usersWithoutPhone = showPhoneAppAdminUi
      ? currentUsers.filter((user) => !user?.phonenumber?.length)
      : [];

    const serviceTab = (
      <div>
        <div className="vertical-flex">
          <GenericCard
            title={t("routeInfo")}
            dismissible={false}
            style={{ paddingBottom: "0px", marginBottom: "20px" }}
          >
            <div className="horizontal-flex">
              <div style={{ flex: 2 }}>
                <CustomSelect
                  options={getRouteTypeOptions()}
                  id="routeType"
                  onInput={inputHandler}
                  label={t("routeType")}
                  initialValue={
                    getRouteTypeOptions().find(
                      (option) => option.value.toString() === loadedItem?.routeType?.toString(),
                    ) || getRouteTypeOptions()[0]
                  }
                  initialValid={true}
                  description={t("routeTypeDescription")}
                ></CustomSelect>
              </div>
              {personelSchoolOptions.length > 1 && (
                <div style={{ flex: 3 }}>
                  <CustomSelect
                    options={personelSchoolOptions}
                    id="school"
                    onInput={inputHandler}
                    label={checkIsPickUp(getRouteTypeValue()) ? t("destination") : t("origin")}
                    initialValue={personelSchoolOptions?.find((option) => option.value === loadedItem?.school?.id)}
                    initialValid={isNewItem ? false : true}
                    description={
                      checkIsPickUp(getRouteTypeValue()) ? t("destinationDescription") : t("originDescription")
                    }
                  ></CustomSelect>
                </div>
              )}
              <div style={{ flex: 3 }}>
                <Input
                  id="name"
                  element="input"
                  type="text"
                  label={t("routeName") + " *"}
                  validators={[VALIDATOR_REQUIRE()]}
                  errorText={t("requireField", { item: t("name") })}
                  initialValue={loadedItem?.name}
                  initialValid={isNewItem ? false : true}
                  onInput={inputHandler}
                  description={
                    showPhoneAppAdminUi ? t("routeNameDescription") : undefined
                  }
                />
              </div>
              <div style={{ flex: 2 }}>
                <Input
                  id="transferStartFinishTime"
                  element="input"
                  type="text"
                  label={
                    checkIsPickUp(getRouteTypeValue())
                      ? t("pickUpRouteClientArrivalTime")
                      : t("dropOffRouteClientArrivalTime")
                  }
                  validators={[VALIDATOR_TIME_EMPTY_ACCEPTED()]}
                  errorText={t("invalidTimeField", {
                    item: checkIsPickUp(getRouteTypeValue())
                      ? t("pickUpRouteClientArrivalTime")
                      : t("dropOffRouteClientArrivalTime"),
                  })}
                  onInput={inputHandler}
                  initialValue={
                    CheckTimeString(loadedItem?.routeClientArrivalTime) ? loadedItem?.routeClientArrivalTime : ""
                  }
                  initialValid={true}
                  description={
                    checkIsPickUp(getRouteTypeValue())
                      ? t("pickUpRouteClientArrivalTimeDescription")
                      : t("dropOffRouteClientArrivalTimeDescription")
                  }
                />
              </div>
              {showPhoneAppAdminUi && (
                <React.Fragment>
                  <div style={{ flex: 2 }}>
                    <CustomSelect
                      options={getYesNoOptions()}
                      id="useUsersDetails"
                      onInput={inputHandler}
                      initialValue={
                        getYesNoOptions().find((option) => option.value === loadedItem?.useUsersDetails) ||
                        getYesNoOptions()[0]
                      }
                      initialValid={true}
                      label={t("showPassengersTime")}
                      description={t("showPassengersTimeDescription")}
                    ></CustomSelect>
                  </div>
                  <div style={{ flex: 2 }}>
                    <CustomSelect
                      options={getIsActiveOptions()}
                      id="isActive"
                      label={t("routeStatus")}
                      description={t("routeStatusDescription")}
                      onInput={inputHandler}
                      initialValue={loadedItem?.isActive === false ? getIsActiveOptions()[0] : getIsActiveOptions()[1]}
                      initialValid={true}
                    ></CustomSelect>
                  </div>
                </React.Fragment>
              )}
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "flex-end",
                  alignSelf: "stretch",
                  paddingBottom: "1.3rem",
                }}
              >
                <CreateOppositeServiceButton
                  loadedItem={loadedItem}
                  isNewItem={isNewItem}
                  InsertItem={InsertItem}
                  gotoItem={gotoItem}
                  serviceVariant="personelservice"
                  config={config}
                />
              </div>
            </div>
          </GenericCard>

          {(config.hasShift || config.hasDate) && (
            <GenericCard title={t("shiftInfo")} style={{ paddingBottom: "0px", marginBottom: "20px" }}>
              {(config.hasShift || config.hasDate) && (
                <div className="horizontal-flex">
                  {config.hasShift && (
                    <CustomSelect
                      options={shiftOptions}
                      id="shift"
                      onInput={inputHandler}
                      initialValue={shiftOptions?.find((option) => option.value === loadedItem?.shift) || null}
                      initialValid={true}
                      label={t("shift.title")}
                    ></CustomSelect>
                  )}

                  {config.hasDate && (
                    <DatePicker
                      id="date"
                      onInput={inputHandler}
                      label={t("serviceDate")}
                      initialValue={loadedItem?.date || ""}
                      initialValid={true}
                    ></DatePicker>
                  )}
                </div>
              )}
            </GenericCard>
          )}

          <GenericCard title={t("serviceInfo")} style={{ paddingBottom: "0px", marginBottom: "20px" }}>
            <div className="horizontal-flex">
              <div style={{ flex: 2 }}>
                <Input
                  id="plate"
                  element="input"
                  type="text"
                  label={t("plateForRoute")}
                  validators={[VALIDATOR_NONE()]}
                  errorText={t("requireField", { item: t("plate") })}
                  onInput={inputHandler}
                  initialValue={loadedItem?.plate}
                  initialValid={isNewItem ? false : true}
                  description={
                    showPhoneAppAdminUi ? t("plateDescription") : undefined
                  }
                />
              </div>

              <div style={{ flex: 3 }}>
                <CustomSelect
                  key={`drivers-${autoAssignTick}`}
                  options={driverOptions}
                  id="drivers"
                  isMulti={true}
                  onInput={inputHandler}
                  label={t("driversForRoute")}
                  initialValue={driverOptions.filter((option) =>
                    currentDrivers.map((driver) => driver.id).includes(option.value),
                  )}
                  initialValid={true}
                  fireChange={(val) => updateDrivers(val)}
                  description={
                    showPhoneAppAdminUi ? t("driversDescription") : undefined
                  }
                ></CustomSelect>
                <AutoAssignButton
                  loadedItem={loadedItem}
                  isNewItem={isNewItem}
                  onAssigned={handleAutoAssigned}
                  style={{ marginBottom: 8 }}
                />
              </div>
              <div style={{ flex: 9.4 }}>
                <CustomSelect
                  options={personelOptions}
                  id="users"
                  isMulti={true}
                  onInput={inputHandler}
                  label={t("passengersIntheRoute")}
                  initialValue={currentUsers.map((user) => ({ value: user.id, label: user.name }))}
                  initialValid={true}
                  disabled={!formState.inputs.school.value}
                  fireChange={(val) => updateCurrentUsers(val)}
                  description={
                    showPhoneAppAdminUi ? t("passengersDescription") : undefined
                  }
                ></CustomSelect>
                {(usersWithoutLocation.length > 0 || usersWithoutPhone.length > 0) && (
                  <div
                    style={{
                      backgroundColor: "#fff3cd",
                      border: "1px solid #ffeaa7",
                      borderRadius: "4px",
                      padding: "12px",
                      marginTop: "20x",
                      marginBottom: "20px",
                      color: "#856404",
                    }}
                  >
                    <strong>{t("warnings")}</strong> <br />
                    {usersWithoutLocation.length > 0 && (
                      <>
                        {t("theseUsersHaveNoLocation")}:{" "}
                        {usersWithoutLocation
                          .map((user) => user?.name || user?.fullname || "Bilinmeyen Kullanıcı")
                          .join(", ")}
                        <br />
                      </>
                    )}
                    {usersWithoutPhone.length > 0 && (
                      <>
                        {t("theseUsersHaveNoPhone")}:{" "}
                        {usersWithoutPhone
                          .map((user) => user?.name || user?.fullname || "Bilinmeyen Kullanıcı")
                          .join(", ")}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </GenericCard>

          {config.serviceType === SERVICE_TYPES.PERSONEL && (
            <GenericCard title={t("serviceDays.cardTitle")} style={{ paddingBottom: "0px", marginBottom: "20px" }}>
              <ServiceDaysPicker
                id="serviceDays"
                onInput={inputHandler}
                initialValue={loadedItem?.serviceDays || []}
                label={t("serviceDays.label")}
              />
            </GenericCard>
          )}
        </div>

        {currentUsers.length > 0 && (
          <React.Fragment>
            {!forceShowMap && (
              <div
                ref={showMapRef}
                style={{
                  display: "flex",
                  justifyContent: "flex-start",
                  marginTop: "10px",
                  marginBottom: "20px",
                  scrollMarginTop: "1rem",
                }}
              >
                <Button type="button" danger size="small" onClick={toggleMap} style={{ minWidth: "200px" }}>
                  {showMap ? t("hideMap") : t("showMap")}
                </Button>
              </div>
            )}

            {showMap && (
              <div style={{ display: "flex", flexDirection: "row", columnGap: "10px", marginTop: "0px" }}>
                <MapWithTransferTimesContainer
                  stops={memoizedStops}
                  origin={memoizedOrigin}
                  type={getRouteTypeValue()}
                  enablePolyline={true}
                  transferStartFinishTime={memoizedTransferStartFinishTime}
                  isFullScreen={true}
                  savedTotalDistanceKm={loadedItem?.totalDistance}
                  savedTotalDurationMin={loadedItem?.totalDuration}
                  onOptimize={(newStops) => {
                    setCurrentUsers(newStops);
                  }}
                />
              </div>
            )}
          </React.Fragment>
        )}

        <FormFooter
          isNewItem={isNewItem}
          onUpdate={itemUpdateSubmitHandler}
          onDelete={handleDeleteClick}
          isUpdating={isLoading}
          disabled={
            !formState.inputs.school.value ||
            !formState.isValid ||
            (config.hasShift && !formState.inputs.shift.value) ||
            (config.hasDate && !formState.inputs.date.value) ||
            isLoading
          }
          extraActions={
            !isNewItem && itemId ? (
              <Button type="button" size="small" onClick={() => setShowHistoryModal(true)}>
                {currentLanguage === "tr" ? "Değişiklik Geçmişi" : "Change History"}
              </Button>
            ) : null
          }
        />
      </div>
    );

    // SMS tabını sadece smsCommunication feature'ı aktifse göster
    if (auth?.company?.features?.[COMPANY_FEATURES.SMS_COMMUNICATION]) {
      const smsTab = (
        <SmsTable users={currentUsers} serviceId={itemId} driver={currentDrivers.length ? currentDrivers[0] : null} />
      );
      return [
        {
          label: t("serviceManagement"),
          content: serviceTab,
        },
        {
          label: t("smsManagement"),
          content: smsTab,
        },
      ];
    }

    // smsCommunication feature'ı aktif değilse, sadece serviceTab döndür (tab yapısı olmadan)
    return serviceTab;
  }, [
    personelSchoolOptions,
    formState.inputs.school.value,
    getRouteTypeValue(),
    formState.inputs.name.value,
    formState.inputs.plate.value,
    formState.inputs.transferStartFinishTime.value,
    formState.inputs.useUsersDetails.value,
    driverOptions,
    currentDrivers,
    currentUsers,
    itemId,
    isNewItem,
    loadedItem,
    inputHandler,
    itemUpdateSubmitHandler,
    handleDeleteClick,
    isLoading,
    t,
    memoizedStops,
    memoizedOrigin,
    memoizedTransferStartFinishTime,
    config,
  ]);

  //standard code
  if (isItemLoaded && !isNewItem && !loadedItem && !error) {
    return <NotFound item={itemType}></NotFound>;
  }

  if (generatedItemId) {
    return (
      <ItemGenerated
        itemType={itemType}
        onGotoNewItem={() => gotoItem(generatedItemId)}
        onCreateNewItem={() => setGeneratedItemId(null)}
        onClose={() => {
          setGeneratedItemId(false);
          onClose();
        }}
      />
    );
  }

  return (
    <React.Fragment>
      <DeleteModal
        endPoint={endPointDelete}
        itemType={itemType}
        itemId={itemId}
        onDeleted={() => {
          DeleteItem(itemId);
          onClose();
        }}
        onClose={() => setShowConfirmModal(false)}
        showModal={showConfirmModal}
      />

      <MessageModal
        showModal={showDeleteBlockedModal}
        title={t("transferPersonelService.deleteBlockedTitle")}
        text={t("transferPersonelService.deleteBlockedHasPassengers")}
        onClose={() => setShowDeleteBlockedModal(false)}
      />

      <AreYouSureModal
        showModal={showRemovePassengersModal}
        title={t("areYouSure")}
        text={t("transferPersonelService.removePassengersConfirm")}
        onClose={() => setShowRemovePassengersModal(false)}
        onConfirmed={performSave}
      />

      {!isNewItem && itemId && (
        <ServiceAuditHistoryModal
          show={showHistoryModal}
          onClose={() => setShowHistoryModal(false)}
          entityType="personelservice"
          entityId={itemId}
        />
      )}

      {/* Show blocker overlay if trying to create new item but route limit is reached */}
      {shouldBlockNewItem && <RouteLimitBlocker limitStatus={limitStatus} onClose={onClose} isOverlay={true} />}

      {isItemLoaded && (
        <form className={shouldBlockNewItem ? "route-limit-blocked-form" : ""}>
          {auth?.company?.features?.[COMPANY_FEATURES.SMS_COMMUNICATION] ? (
            <Tabs tabs={personelServiceItemTabs} tabValue={tabValue} onTabChange={handleTabChange} isVertical={false} />
          ) : (
            personelServiceItemTabs
          )}
        </form>
      )}
    </React.Fragment>
  );
};

export default BasePersonelServiceItem;
